/**
 * กระบี่วงธารา (drift-sword), hand-made in the sample style (was the C3 generator skeleton). The art's own look: a
 * sword drawn in unbroken circles that carry water — azure and white blade bands on round paths, rings of water on the
 * ground, falling and suspended droplets, the ink/white yin-yang pair as its scatter mark, and the taiji disc of its
 * sibling art's master page (sampleArt/taiji.ts `taijiDisc`, gold rim) for the still and the sealing moves. Every move
 * is staged anticipation -> strike -> impact -> dissipation and has its own shape and timing:
 *   ts-circle  an upright circle cut closing on the foe, breaking into rain;
 *   ts-yinyang (break) ink and white halves joined over the raised blade, a falling cleave that parts the water;
 *   ts-stars   seven stars of the Dipper lit over the caster, falling as short streaks on the marked area;
 *   ts-still   (heal) ripples running inward to a still pool, one drop from the crown, motes rising;
 *   ts-flow    two linked cuts drawing the taiji S on the foe, a stream carrying the drops away;
 *   ts-thread  a dash leaving a fine thread that trembles and turns into dew beads;
 *   ts-focus   (buff) droplets lifted off a blade-ticked ring and held motionless round the waist;
 *   ts-heaven  (ท่าใหญ่) a set piece: the area disc formed, three great water cuts, a dome sealing it, rain after.
 * Original designs (no reference expression).
 */
import { add, blade, crescent, diamond, hash, isoRing, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { taijiDisc } from '../taiji';
import {
  bladeArc,
  C,
  clamp01,
  fade,
  hitStar,
  isoDisc,
  level,
  puffs,
  ringOn,
  since,
  strikeAt,
  tilesPx,
  type ArtFx,
} from '../families/swordKit';

const up = (d: V): V => (d[0] >= 0 ? [d[1], -d[0]] : [-d[1], d[0]]);

// ── The art's pieces ──

/** A water drop: a white cell over an azure one (a 1-wide glint, no outline). */
function drop(g: G, x: number, y: number, dark = false): void {
  px(g, x, y, dark ? C('azure', 5) : C('white', 8));
  px(g, x, y + 1, C('azure', dark ? 3 : 4));
}

/** Drops flung from `c` around angle `dir` (radians, screen) within `spread`, falling under gravity, dithered out. */
function spray(g: G, c: V, e: number, n: number, dir: number, spread: number, speed: number, seed: number, life = 9, grav = 0.3): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.5, life * 0.8));
  for (let i = 0; i < n; i++) {
    const a = dir + (hash(i, seed) - 0.5) * spread;
    const v = speed * (0.6 + hash(i, seed + 1) * 0.8);
    drop(g, c[0] + Math.cos(a) * v * e, c[1] + Math.sin(a) * v * e + grav * e * e, i % 3 === 0);
  }
  g.globalAlpha = 1;
}

/** A ring of water on the ground at `c` spreading over `life` frames: thick white-lipped while fresh, then azure. */
function ripple(g: G, c: V, e: number, r0: number, speed: number, life = 7): void {
  if (e < 0 || e >= life) return;
  const r = r0 + e * speed;
  level(g, fade(e, life * 0.4, life * 0.75));
  isoRing(g, c[0], c[1], r, e < 2 ? C('white', 7) : C('azure', 5), e < 2);
  if (e < life * 0.6) isoRing(g, c[0], c[1] + 1, Math.max(1, r - 2), C('azure', 3), false, 0, Math.PI);
  g.globalAlpha = 1;
}

/** The art's scatter mark: an ink dot with a white eye beside a white dot with an ink eye (yin and yang). */
function yyPair(g: G, x: number, y: number): void {
  x = Math.round(x);
  y = Math.round(y);
  g.fillStyle = C('ink', 1);
  g.fillRect(x - 2, y, 2, 2);
  g.fillStyle = C('white', 7);
  g.fillRect(x, y, 2, 2);
  px(g, x - 2, y, C('white', 8));
  px(g, x + 1, y + 1, C('ink', 1));
}

/** Yin-yang pairs flung out from `c` and drifting (no gravity: they float), dithered out. */
function pairs(g: G, c: V, e: number, n: number, seed: number, life = 10): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.5, life * 0.8));
  for (let i = 0; i < n; i++) {
    const a = hash(i, seed) * Math.PI * 2;
    const v = 1.6 + hash(i, seed + 2) * 1.6;
    yyPair(g, c[0] + Math.cos(a) * v * e, c[1] + Math.sin(a) * v * e * 0.6 - e * 0.4);
  }
  g.globalAlpha = 1;
}

/**
 * A blade band on an upright ellipse round `o` (rx, ry) from angle `a0` to the head at `a1` (screen angles): the head
 * `w` rows deep (white outer row, azure inside), thinning to one row and dithering toward the tail.
 */
function loopBand(g: G, o: V, rx: number, ry: number, a0: number, a1: number, w: number, lv = 3): void {
  const n = Math.max(6, Math.ceil(Math.abs(a1 - a0) * Math.max(rx, ry) * 1.2));
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const a = a0 + (a1 - a0) * u;
    const rows = Math.max(1, Math.round(1 + (w - 1) * u * u));
    level(g, Math.min(lv, u < 0.25 ? 1 : u < 0.55 ? 2 : 3));
    for (let k = rows - 1; k >= 0; k--) {
      const col = k === 0 ? (u > 0.8 ? C('white', 8) : C('azure', 6)) : C('azure', k === 1 ? 5 : 4);
      px(g, o[0] + Math.cos(a) * (rx - k), o[1] + Math.sin(a) * (ry - k), col);
    }
  }
  g.globalAlpha = 1;
}

/** The tip glint held before a blow, swelling. */
function glint(g: G, s: Scene, t0: number, t1: number): void {
  if (s.t < t0 || s.t >= t1) return;
  const { tip } = s.limbs(s.t);
  level(g, 3);
  diamond(g, tip[0], tip[1], s.t < (t0 + t1) / 2 ? 1 : 2, C('azure', 5), C('white', 8));
  g.globalAlpha = 1;
}

// ── 1. ts-circle: the blade drawn in one unbroken circle round the space to the foe, closing, then raining down ──

const CIRCLE_HIT = 0.6;
const CIRCLE_SHUT = 0.8;
/** The circle stands between the caster and the foe, its far edge through the foe's chest (the stream it embraces). */
const circleAt = (s: Scene): { o: V; rx: number; ry: number } => {
  const D = Math.hypot(s.f[0] - s.a[0], s.f[1] - s.a[1]);
  const rx = Math.max(18, Math.min(40, D / 2 - 1));
  return {
    o: [s.a[0] + s.d[0] * (D - rx - 3), (s.a[1] + s.f[1]) / 2 - s.chest],
    rx,
    ry: Math.round(Math.min(rx * 0.85, s.chest * 0.9)),
  };
};
const circle: ArtFx = {
  id: 'taiji-circle',
  skill: 'ts-circle',
  th: 'ts-circle',
  on: true,
  note: 'วงน้ำวาดรอบเท้า ประกายปลายกระบี่ -> กระบี่วาดวงกลมตั้งใหญ่ไม่ขาดสายจากข้างตัวขึ้นข้ามหัวผ่านเป้า (ดาวกระแทก หยดน้ำสาด คู่หยินหยางกระเด็น) วนต่อจนปิดวง -> วงสว่างวาบ -> วงแตกเป็นหยดน้ำร่วงลงพื้นเป็นวงน้ำเล็ก',
  weapon: 'sword',
  acts: [{ m: 'wxCleave', t0: 0.2, t1: 1.0 }],
  hits: [CIRCLE_HIT],
  big: CIRCLE_HIT,
  pre: 0.4,
  hit: CIRCLE_HIT,
  end: 1.95,
  afterimage: null,
  under(g, s) {
    // Anticipation: a small water ring drawn round the feet.
    if (s.t >= 0.2 && s.t < 0.75) {
      level(g, s.t < 0.6 ? 2 : 1);
      ringOn(g, s.a, 12, Math.PI * 0.5, (s.t - 0.2) / 0.3, C('azure', 5));
      g.globalAlpha = 1;
    }
    // Impact: two rings at the foe's feet; the falling rain makes small rings under the circle.
    const h = since(s.t, CIRCLE_HIT);
    ripple(g, s.f, h, 6, 4, 7);
    ripple(g, s.f, h - 2, 4, 3, 6);
    const { o, rx } = circleAt(s);
    for (let i = 0; i < 5; i++) {
      const e = since(s.t, 1.45 + hash(i, 6) * 0.2);
      ripple(g, [o[0] - rx * 0.8 + i * rx * 0.4, s.a[1] + 3 - (i % 2) * 4], e, 2, 1.5, 5);
    }
  },
  over(g, s) {
    glint(g, s, 0.22, 0.42);
    bladeArc(g, s, 0.42, 0.7, 'steel', 2);
    const { o, rx, ry } = circleAt(s);
    // The circle: the head starts at the caster's side, runs up over the top, down through the foe (the blow, half way)
    // and on round under to where it began; the tail thins behind it.
    const k = clamp01((s.t - 0.4) / (CIRCLE_SHUT - 0.4));
    if (s.t >= 0.4 && s.t < CIRCLE_SHUT) {
      const head = Math.PI + k * Math.PI * 2;
      loopBand(g, o, rx, ry, Math.max(Math.PI, head - Math.PI * 1.3), head, k < 0.3 ? 3 : 4);
    }
    // Closed: the whole ring flashes white, held, then thins before it breaks.
    const h = since(s.t, CIRCLE_SHUT);
    if (h >= 0 && h < 4) {
      level(g, h < 2 ? 3 : 2);
      const n = Math.round((rx + ry) * 3.5);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        px(g, o[0] + Math.cos(a) * rx, o[1] + Math.sin(a) * ry, h < 1 ? C('white', 8) : C('azure', 6));
        if (h < 2) px(g, o[0] + Math.cos(a) * (rx - 1), o[1] + Math.sin(a) * (ry - 1), C('azure', 4));
      }
      g.globalAlpha = 1;
    }
    if (h >= 4 && h < 15) {
      // The ring breaks: its drops fall under gravity to the ground, dithering out as they land.
      const e = h - 4;
      for (let i = 0; i < 20; i++) {
        const a = (i / 20) * Math.PI * 2 + 0.2;
        const y0 = o[1] + Math.sin(a) * ry;
        const y = y0 + 0.55 * e * e * (0.7 + hash(i, 4) * 0.6);
        if (y > s.a[1] + 2) continue;
        level(g, fade(e, 6, 9));
        drop(g, o[0] + Math.cos(a) * (rx + e * 0.5), y, i % 2 === 0);
      }
      g.globalAlpha = 1;
    }
    // Impact at the foe as the head passes it: a star, drops thrown on down the circle, the yin-yang pairs.
    const c = strikeAt(s);
    const hh = since(s.t, CIRCLE_HIT);
    hitStar(g, s, c, hh, 'azure', 1.1);
    spray(g, c, hh, 10, 0.5, 1.4, 2.4, 7);
    pairs(g, c, hh - 1, 3, 9, 9);
  },
};

// ── 2. ts-yinyang (break): ink and white halves joined over the raised blade, a falling cleave parting the water ──

const YY_HIT = 0.8;
const yinyang: ArtFx = {
  id: 'taiji-yinyang',
  skill: 'ts-yinyang',
  th: 'ts-yinyang',
  on: true,
  note: 'วงอำพันที่เท้า (สัญญาณท่าทำลาย) ครึ่งหมึกครึ่งขาววนเข้ามารวมเป็นวงหยินหยางเหนือกระบี่ที่ยกสูง -> ผ่าลงเป็นแนวตั้ง เงาร่างแยก -> วงหยินหยางแตกเป็นสองซีกกระเด็นออกข้าง สายน้ำบนพื้นแยกออกสองฝั่ง -> หยดน้ำร่วง จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.62 },
    { m: 'wxCleave', t0: 0.62, t1: 1.1 },
  ],
  hits: [YY_HIT],
  big: YY_HIT,
  ghosts: [0.68, 0.74],
  stepIn: 6,
  cast: [0.1, 0.62],
  pre: 0.55,
  hit: YY_HIT,
  end: 1.9,
  afterimage: 'port',
  under(g, s) {
    // The guard-break tell: an amber ring drawn on round the feet, an azure ring inside.
    if (s.t >= 0.1 && s.t < YY_HIT) {
      level(g, 3);
      ringOn(g, s.a, 14, -Math.PI / 2, clamp01((s.t - 0.1) / 0.35), C('fire', 4), true);
      level(g, 2);
      ringOn(g, s.a, 10, Math.PI / 2, clamp01((s.t - 0.25) / 0.3), C('azure', 5));
      g.globalAlpha = 1;
    }
    // The parted water: a groove along the cut through the foe's feet, two banks of water pushed apart from it.
    const h = since(s.t, YY_HIT);
    if (h < 0 || h > 13) return;
    const c = s.f;
    const lv = fade(h, 7, 10);
    level(g, lv);
    line(g, add(c, s.d, -18), add(c, s.d, 22), C('ink', 2));
    line(g, add(add(c, s.d, -14), [0, 1], 1), add(c, s.d, 18), C('azure', 2));
    const gap = 2 + Math.min(10, h * 1.6);
    for (const side of [-1, 1]) {
      for (let x = -16; x <= 20; x += 2) {
        const wave = Math.round(Math.sin(x * 0.5 + h) * 0.8);
        const p = add(c, s.d, x);
        const y = p[1] + side * (gap * 0.5) + wave;
        px(g, p[0], y, (x + h) % 6 === 0 ? C('white', 8) : C('azure', side < 0 ? 5 : 4));
        if (h < 6) px(g, p[0], y - side, C('azure', 3));
      }
    }
    g.globalAlpha = 1;
  },
  over(g, s) {
    // Over the crown, a little forward: where the halves meet.
    const over: V = [s.a[0] + s.d[0] * 8, s.a[1] - s.chest * 2.25];
    // Gathering: an ink half and a white half circling in from either side to meet over the raised blade.
    if (s.t >= 0.1 && s.t < 0.5) {
      const k = clamp01((s.t - 0.1) / 0.4);
      for (let j = 0; j < 2; j++) {
        const a = (j ? 0 : Math.PI) + k * Math.PI * 1.5;
        const r = 26 * (1 - k) + 3;
        const p: V = [over[0] + Math.cos(a) * r, over[1] + Math.sin(a) * r * 0.5];
        const tail: V = [over[0] + Math.cos(a - 0.6) * (r + 4), over[1] + Math.sin(a - 0.6) * (r + 4) * 0.5];
        level(g, k < 0.2 ? 2 : 3);
        line(g, tail, p, j ? C('white', 6) : C('ink', 3));
        g.fillStyle = j ? C('white', 7) : C('ink', 1);
        g.fillRect(Math.round(p[0]) - 1, Math.round(p[1]) - 1, 3, 3);
        px(g, p[0], p[1], j ? C('ink', 1) : C('white', 8));
      }
      g.globalAlpha = 1;
    }
    // Joined: a small turning taiji disc over the tip (flash as it closes), held until the cleave.
    if (s.t >= 0.5 && s.t < YY_HIT - 1 / 12) {
      const e = since(s.t, 0.5);
      taijiDisc(g, over, 10, s.t * 4, e < 1 ? 2 : 3, e < 1 ? 1 : 0, false, e >= 1);
      if (e < 2) {
        level(g, 3);
        diamond(g, over[0], over[1] - 5, e === 0 ? 2 : 1, C('azure', 5), C('white', 8));
        g.globalAlpha = 1;
      }
    }
    bladeArc(g, s, 0.66, 0.95, 'azure', 4);
    // The cleave: a tall falling band through the foe, drawn top to bottom over two frames, held, then thinned.
    const c = strikeAt(s);
    const e = since(s.t, YY_HIT - 1 / 12);
    if (e >= 0 && e < 9) {
      const top: V = [c[0] + 3, c[1] - s.chest * 1.3];
      const bot: V = [c[0] - 2, s.f[1] + 2];
      const z = e === 0 ? lerp(top, bot, 0.5) : bot;
      level(g, e < 3 ? 3 : fade(e, 3, 6));
      if (e < 3) blade(g, top, z, 4, C('white', 8), C('azure', 4), 0.55);
      else line(g, top, z, C('azure', 5));
      g.globalAlpha = 1;
      // The joined disc rides the cleave down onto the foe.
      if (e < 1) taijiDisc(g, lerp(top, c, 0.4), 10, s.t * 4, 3, 0, false, true);
    }
    // Impact: the disc split in two, the ink half flung back-left, the white half forward-right, both dithering out.
    const h = since(s.t, YY_HIT);
    hitStar(g, s, c, h, 'azure', 1.3);
    if (h >= 0 && h < 9) {
      level(g, fade(h, 4, 7));
      for (let j = 0; j < 2; j++) {
        const sx = j ? 1 : -1;
        const p: V = [c[0] + sx * (4 + h * 3), c[1] - 2 + h * h * 0.15];
        for (let y = -3; y <= 3; y++)
          for (let x = 0; x <= 3; x++) {
            if (x * x + y * y > 10) continue;
            px(g, p[0] + sx * x, p[1] + y, j ? C('white', x === 0 ? 8 : 7) : C('ink', x === 0 ? 3 : 1));
          }
      }
      g.globalAlpha = 1;
    }
    spray(g, c, h, 5, -Math.PI / 2 - 0.9, 0.8, 2.6, 13, 10, 0.35);
    spray(g, c, h, 5, -Math.PI / 2 + 0.9, 0.8, 2.6, 17, 10, 0.35);
  },
};

// ── 3. ts-stars: the seven stars of the Dipper lit over the caster, falling as short streaks on the marked area ──

/** The Dipper's seven stars, handle to bowl (x -1..1 across, y down), our own layout. */
const DIPPER: readonly V[] = [
  [-1.0, -0.35],
  [-0.66, -0.12],
  [-0.36, -0.04],
  [-0.06, 0.06],
  [0.02, 0.5],
  [0.62, 0.56],
  [0.66, 0.08],
];
const STARS_HIT = 0.85;
const starFall = (i: number): number => 0.7 + i * 0.035;
const skyAt = (s: Scene, i: number): V => [
  s.a[0] + s.d[0] * 6 + DIPPER[i]![0] * 18,
  s.a[1] - s.chest * 2.2 - 8 + DIPPER[i]![1] * 10,
];
const STARS_R = Math.min(56, tilesPx(2.2));
const groundAt = (s: Scene, i: number): V => [s.f[0] + DIPPER[i]![0] * STARS_R * 0.75, s.f[1] + DIPPER[i]![1] * STARS_R * 0.45];

const stars: ArtFx = {
  id: 'taiji-stars',
  skill: 'ts-stars',
  th: 'ts-stars',
  on: true,
  note: 'ชี้กระบี่ขึ้นฟ้า ดาวเจ็ดดวงเรียงเป็นกระบวยติดขึ้นทีละดวงเหนือหัว เส้นดาวเชื่อม วงพื้นและจุดดาวบนพื้นบอกเขต -> ฟันลง ดาวร่วงเป็นสายสั้นทีละดวงลงจุดบนพื้น -> น้ำกระเซ็นทุกจุด ลายดาวบนพื้นสว่างเชื่อมกัน -> จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.1, t1: 0.62 },
    { m: 'wxCleave', t0: 0.62, t1: 1.1 },
  ],
  hits: [STARS_HIT],
  big: STARS_HIT,
  pre: 0.5,
  hit: STARS_HIT,
  end: 2.0,
  afterimage: null,
  under(g, s) {
    // The tell: the area ring drawn on at the target, a small dithered ring where each star will land.
    if (s.t >= 0.4 && s.t < 1.6) {
      level(g, s.t < 1.2 ? 2 : 1);
      ringOn(g, s.f, STARS_R, -Math.PI / 2, (s.t - 0.4) / 0.25, C('azure', 4), s.t < 0.9);
      for (let i = 0; i < 7; i++) if (s.t >= 0.45 + i * 0.03 && s.t < starFall(i) + 0.1) {
        const p = groundAt(s, i);
        isoRing(g, p[0], p[1], 3, C('azure', 5));
      }
      g.globalAlpha = 1;
    }
    // Impacts: a ring at each landing; then the constellation lit on the ground, joined, and dithered away.
    for (let i = 0; i < 7; i++) {
      const e = since(s.t, starFall(i) + 0.1);
      ripple(g, groundAt(s, i), e, 3, 2.5, 6);
    }
    const e = since(s.t, 1.0);
    if (e >= 0 && e < 10) {
      level(g, fade(e, 4, 7));
      for (let i = 0; i < 6; i++) line(g, groundAt(s, i), groundAt(s, i + 1), C('azure', 5));
      for (let i = 0; i < 7; i++) {
        const p = groundAt(s, i);
        diamond(g, p[0], p[1], e < 3 ? 2 : 1, C('azure', 5), C('white', 8));
      }
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    glint(g, s, 0.2, 0.4);
    // The sky: each star lit in turn over the caster, the Dipper's line between them dithered.
    if (s.t >= 0.3 && s.t < 1.05) {
      for (let i = 0; i < 7; i++) {
        const born = 0.3 + i * 0.05;
        if (s.t < born || s.t >= starFall(i)) continue;
        const p = skyAt(s, i);
        if (i > 0 && s.t >= born) {
          level(g, 1);
          line(g, skyAt(s, i - 1), p, C('azure', 5));
        }
        level(g, 3);
        diamond(g, p[0], p[1], s.t - born < 0.08 ? 1 : 2, C('azure', 5), C('white', 8));
        if (since(s.t, born) === 0) {
          line(g, [p[0] - 4, p[1]], [p[0] + 4, p[1]], C('white', 8));
          line(g, [p[0], p[1] - 3], [p[0], p[1] + 3], C('white', 8));
        }
      }
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.64, 0.95, 'azure', 3);
    // The fall: each star a short streak (a packet, never a line from sky to ground) over three frames.
    for (let i = 0; i < 7; i++) {
      const at = starFall(i);
      if (s.t < at || s.t >= at + 0.25) continue;
      const k = clamp01((s.t - at) / 0.1);
      const from = skyAt(s, i);
      const to: V = [groundAt(s, i)[0], groundAt(s, i)[1] - 1];
      const head = lerp(from, to, k);
      const tail = lerp(from, to, Math.max(0, k - 0.3));
      if (k < 1) {
        level(g, 3);
        blade(g, tail, head, 2, C('white', 8), C('azure', 4), 0.85);
        diamond(g, head[0], head[1], 1, C('azure', 6), C('white', 8));
      }
      const e = since(s.t, at + 0.1);
      spray(g, to, e, 4, -Math.PI / 2, 1.8, 1.8, 20 + i, 6, 0.4);
    }
    // The blow on the foe: a star; yin-yang pairs float up off the constellation.
    const h = since(s.t, STARS_HIT);
    hitStar(g, s, strikeAt(s), h, 'azure', 1);
    pairs(g, [s.f[0], s.f[1] - 4], since(s.t, 1.0), 4, 23, 11);
  },
};

// ── 4. ts-still (heal): ripples running inward to a still pool, one drop from the crown, motes rising ──

const still: ArtFx = {
  id: 'taiji-still',
  skill: 'ts-still',
  th: 'ts-still',
  on: true,
  note: 'วงน้ำไหลย้อนเข้าหาเท้า น้ำนิ่งเป็นแอ่ง -> หยดน้ำหยดเดียวก่อตัวเหนือศีรษะแล้วหล่นผ่านร่าง -> กระทบแอ่ง วงคลื่นเดียวแผ่ออก วงหยินหยางบนผิวน้ำหมุนช้า -> ฟื้นพลัง หยดน้ำลอยขึ้นทวนรอบตัว -> จางเป็นไอ',
  weapon: 'sword',
  acts: [{ m: 'wxHeal', t0: 0.1, t1: 2.4 }],
  hits: [],
  pre: 0.1,
  hit: 0.9,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    if (s.t < 0.15 || s.t > 2.45) return;
    const lv = s.t < 2.0 ? 3 : s.t < 2.25 ? 2 : 1;
    // The pool: a faint disc of still water, rimmed.
    const r = Math.round(8 + clamp01((s.t - 0.15) / 0.5) * 22);
    isoDisc(g, s.a, r, C('azure', 3), C('azure', 5), Math.min(lv, 1));
    level(g, Math.min(lv, 2));
    isoRing(g, s.a[0], s.a[1], r, C('azure', 5));
    // Stillness: three ripples running INWARD to the feet (the motion subdued), before the drop.
    for (let j = 0; j < 3; j++) {
      const k = clamp01((s.t - 0.2 - j * 0.12) / 0.4);
      if (k <= 0 || k >= 1) continue;
      level(g, k < 0.2 ? 2 : 3);
      isoRing(g, s.a[0], s.a[1], 40 - k * 30, j === 1 ? C('white', 7) : C('azure', 5), k < 0.5);
    }
    g.globalAlpha = 1;
    // The drop lands: one ring out across the pool and past it.
    ripple(g, s.a, since(s.t, 0.9), 4, 5, 8);
    // The calm after: the taiji disc on the water, turning very slowly, its gold rim drawn on.
    if (s.t >= 0.95) {
      const e = since(s.t, 0.95);
      const fill = e < 1 || s.t > 2.15 ? 1 : 2;
      taijiDisc(g, s.a, 20, (s.t - 0.95) * 0.7, fill, s.t < 2.1 ? clamp01((e + 1) / 4) : 0, e < 1, s.t < 2.15);
    }
  },
  over(g, s) {
    const crown: V = [s.a[0] + s.d[0] * 2, s.a[1] - s.chest * 2.15];
    // The drop: a glint gathers over the crown, swells, falls through the body to the pool.
    if (s.t >= 0.45 && s.t < 0.9) {
      const k = clamp01((s.t - 0.7) / 0.2);
      const y = crown[1] + k * k * (s.a[1] - 3 - crown[1]);
      level(g, 3);
      if (s.t < 0.58) diamond(g, crown[0], y, 1, C('azure', 5), C('white', 8));
      else {
        g.fillStyle = C('azure', 5);
        g.fillRect(Math.round(crown[0]) - 1, Math.round(y) - 1, 3, 3);
        px(g, crown[0], y - 2, C('white', 8));
        px(g, crown[0] - 1, y - 1, C('white', 8));
        px(g, crown[0], y + 2, C('azure', 4));
        if (k > 0) {
          level(g, 2);
          line(g, [crown[0], y - 3], [crown[0], y - 3 - k * 10], C('azure', 5));
        }
      }
      g.globalAlpha = 1;
    }
    // The splash where it lands.
    spray(g, [s.a[0], s.a[1] - 1], since(s.t, 0.9), 10, -Math.PI / 2, 2.4, 2, 31, 7, 0.45);
    // Healing: drops rising slowly round the body (rain run backward), a few yin-yang pairs among them.
    const e = since(s.t, 0.95);
    if (e >= 0 && s.t < 2.3) {
      const lv = s.t < 1.95 ? 3 : 2;
      const span = s.chest * 2.2;
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + 0.3;
        const x = s.a[0] + Math.cos(a) * (14 + hash(i, 33) * 6);
        const y = s.a[1] + Math.sin(a) * 5 - 3 - ((e * 1.6 + i * 9) % span);
        level(g, y < s.a[1] - span * 0.75 ? Math.min(lv, 2) : lv);
        if (i % 4 === 3) yyPair(g, x, y);
        else {
          g.fillStyle = C('azure', 5);
          g.fillRect(Math.round(x), Math.round(y), 2, 2);
          px(g, x, y, C('white', 8));
          px(g, x, y + 2, C('azure', 3));
        }
      }
      g.globalAlpha = 1;
    }
    // Dissipation: the pool let go as a low mist.
    puffs(g, [s.a[0], s.a[1] - 3], since(s.t, 2.15), 10, 14, 2.5, 'white', 6, 0.5, 2);
  },
};

// ── 5. ts-flow: two linked cuts drawing the taiji S on the foe, a stream carrying the drops away ──

const FLOW_HITS = [0.45, 0.85] as const;
const FLOW_R = 22;
const flow: ArtFx = {
  id: 'taiji-flow',
  skill: 'ts-flow',
  th: 'ts-flow',
  on: true,
  note: 'ฟันแรกวาดครึ่งบนของเส้นโค้งหยินหยาง -> ฟันที่สองต่อเป็นครึ่งล่าง ได้เป็นตัว S เต็ม เงาร่างแยกทั้งสองจังหวะ -> วงหยินหยางเต็มวงปรากฏชั่วครู่พร้อมตาสองจุด -> แตกเป็นหยดน้ำไหลตามสายน้ำบนพื้นออกไป',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.2, t1: 0.55 },
    { m: 'wxRise', t0: 0.55, t1: 0.72 },
    { m: 'wxCleave', t0: 0.72, t1: 1.1 },
  ],
  hits: [...FLOW_HITS],
  big: FLOW_HITS[1],
  ghosts: [0.4, 0.8],
  stepIn: 8,
  pre: 0.3,
  hit: FLOW_HITS[0],
  end: 1.95,
  afterimage: 'port',
  under(g, s) {
    // The stream: a meandering line of water on the ground from the caster's feet past the foe, glints flowing along.
    if (s.t < 0.35 || s.t > 1.8) return;
    const L = Math.hypot(s.f[0] - s.a[0], s.f[1] - s.a[1]) + 30;
    const k = clamp01((s.t - 0.35) / 0.3);
    const lv = s.t < 1.4 ? 2 : s.t < 1.6 ? 1 : 0;
    if (lv <= 0) return;
    const fr = since(s.t, 0.35);
    for (let x = 6; x < 6 + (L - 6) * k; x += 1) {
      const p = add(s.a, s.d, x);
      const y = p[1] + 3 + Math.round(Math.sin(x * 0.16) * 2.2);
      level(g, lv);
      px(g, p[0], y, C('azure', 4));
      if ((x + fr * 4) % 11 === 0) {
        level(g, 3);
        px(g, p[0], y - 1, C('white', 8));
      }
    }
    g.globalAlpha = 1;
  },
  over(g, s) {
    glint(g, s, 0.22, 0.36);
    bladeArc(g, s, 0.3, 0.55, 'azure', 3);
    bladeArc(g, s, 0.7, 0.95, 'azure', 3);
    const c = strikeAt(s, 1.05);
    const r = FLOW_R / 2;
    const top: V = [c[0], c[1] - r];
    const bot: V = [c[0], c[1] + r];
    // Cut 1: the upper half of the S (round the right side of the upper small circle), cut 2 the lower (round the left).
    const draw = (o: V, a0: number, sweep: number, at: number): void => {
      const e = since(s.t, at - 0.08);
      if (e < 0 || e >= 11) return;
      const k = clamp01((s.t - (at - 0.08)) / 0.08);
      const a1 = a0 + sweep * k;
      // Fresh: a solid stroke 3 deep at its lead (white edge); then a thin azure line, dithered away.
      level(g, e < 5 ? 3 : e < 8 ? 2 : 1);
      if (e < 5) crescent(g, o[0], o[1], r + 1, a0, a1, 3, C('white', 8), C('azure', 5), 1, true);
      else crescent(g, o[0], o[1], r + 1, a0, a1, 1, C('azure', 6), C('azure', 4), 1, false);
      g.globalAlpha = 1;
    };
    draw(top, -Math.PI / 2, Math.PI, FLOW_HITS[0]);
    draw(bot, -Math.PI / 2, -Math.PI, FLOW_HITS[1]);
    // The whole sign: the outer circle and the two eyes shown for a moment after the second cut.
    const w = since(s.t, FLOW_HITS[1]);
    if (w >= 1 && w < 7) {
      level(g, w < 4 ? 3 : 2);
      // The S whole (both cuts' halves), then the circle round it.
      crescent(g, top[0], top[1], r + 1, -Math.PI / 2, Math.PI / 2, 2, C('white', 8), C('azure', 5), 1, false);
      crescent(g, bot[0], bot[1], r + 1, -Math.PI / 2, -Math.PI * 1.5, 2, C('white', 8), C('azure', 5), 1, false);
      const n = 160;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        px(g, c[0] + Math.cos(a) * (FLOW_R + 1), c[1] + Math.sin(a) * (FLOW_R + 1), C('azure', 5));
      }
      level(g, 3);
      g.fillStyle = C('white', 8);
      g.fillRect(Math.round(top[0]), Math.round(top[1]), 2, 2);
      g.fillStyle = C('ink', 1);
      g.fillRect(Math.round(bot[0]) - 1, Math.round(bot[1]), 2, 2);
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, since(s.t, FLOW_HITS[0]), 'azure', 0.8);
    hitStar(g, s, c, since(s.t, FLOW_HITS[1]), 'azure', 1.1);
    spray(g, c, since(s.t, FLOW_HITS[0]), 5, -0.4, 1.0, 2.2, 41, 8);
    // Dissipation: the sign breaks into drops carried on downstream (along the strike), wavering and falling.
    const e = since(s.t, FLOW_HITS[1] + 0.45);
    if (e >= 0 && e < 12) {
      level(g, fade(e, 6, 9));
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        const x = c[0] + Math.cos(a) * FLOW_R + s.d[0] * e * (2 + hash(i, 43) * 1.5);
        const y = c[1] + Math.sin(a) * FLOW_R + Math.sin(e * 0.8 + i) * 1.2 + e * e * 0.12;
        if (i % 5 === 0) yyPair(g, x, y);
        else drop(g, x, y, i % 2 === 0);
      }
      g.globalAlpha = 1;
    }
  },
};

// ── 6. ts-thread: a dash leaving a fine thread that trembles and turns into dew beads ──

const THREAD = 104;
const THREAD_HIT = 0.63;
const thread: ArtFx = {
  id: 'taiji-thread',
  skill: 'ts-thread',
  th: 'ts-thread',
  on: true,
  note: 'ย่อตัว ปลายกระบี่ชี้ เส้นด้ายบางยื่นออกนำทาง -> พุ่งเป็นเส้นตรงทะลุเป้า เงาร่างแยก เส้นด้ายขาวบางลากตามหลังเฉพาะช่วงใกล้ตัว -> รอยเฉือนเส้นเดียวผ่านเป้า -> เส้นด้ายสั่นแล้วกลายเป็นหยดน้ำค้างเรียงแถว ร่วงลงพื้นเป็นวงเล็ก',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.2 }],
  hits: [THREAD_HIT],
  big: THREAD_HIT,
  ghosts: [0.53, 0.57, 0.61],
  pre: 0.45,
  hit: THREAD_HIT,
  end: 1.9,
  dash: THREAD,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - 0.5) / 0.22);
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 2.0) / 0.3);
    return [s.d[0] * THREAD * k * back, s.d[1] * THREAD * k * back];
  },
  under(g, s) {
    // The kick-off: a ring of water off the start, a scuff of spray.
    ripple(g, s.a, since(s.t, 0.48), 4, 3, 6);
    // The path: a fine dark cut on the ground under the thread, dithered away.
    const e = since(s.t, 0.5);
    if (e < 0 || e > 16) return;
    const b = s.body(Math.min(s.t, 0.75));
    level(g, fade(e, 8, 12) - 1);
    line(g, add(s.a, s.d, 6), add(b, s.d, -4), C('azure', 2));
    g.globalAlpha = 1;
    // Where the beads fall: small rings along the path.
    for (let i = 0; i < 6; i++) {
      const p = add(s.a, s.d, 12 + i * 15);
      ripple(g, p, since(s.t, 1.32 + hash(i, 51) * 0.2), 2, 1.5, 5);
    }
  },
  over(g, s) {
    const u = up(s.d);
    // Anticipation: the tip held low with a glint, a fine thread spun out ahead of it.
    if (s.t >= 0.32 && s.t < 0.52) {
      const { tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.32) / 0.15);
      level(g, 2);
      line(g, tip, add(tip, s.d, 4 + k * 16), C('white', 8));
      level(g, 3);
      diamond(g, tip[0], tip[1], s.t > 0.42 ? 2 : 1, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    // The thread at waist height from the start to the body: solid only near the body (never a tube), beads behind.
    const e = since(s.t, 0.5);
    if (e >= 0 && e < 18) {
      const h = s.chest * 0.9;
      const start = add(add(s.a, s.d, 4), u, h);
      const b = add(add(s.body(Math.min(s.t, 0.8)), s.d, -6), u, h);
      const L = Math.hypot(b[0] - start[0], b[1] - start[1]);
      const tremble = since(s.t, THREAD_HIT + 0.04);
      const amp = tremble >= 0 && tremble < 5 ? [2, -2, 1, -1, 0][tremble]! : 0;
      const beadAge = since(s.t, 0.9);
      for (let x = 0; x <= L; x += 1) {
        const p = add(start, s.d, x);
        const wav = Math.round(Math.sin((x / Math.max(1, L)) * Math.PI) * amp);
        const near = L - x < 36 && s.t < 0.95;
        if (near) {
          level(g, 3);
          px(g, p[0], p[1] + wav, L - x < 10 ? C('white', 8) : C('azure', 6));
          if (L - x < 24) px(g, p[0], p[1] + wav + 1, C('azure', 4));
          continue;
        }
        if (x % 6 !== 0) {
          if (beadAge < 0) {
            level(g, 2);
            px(g, p[0], p[1] + wav, C('azure', 5));
          }
          continue;
        }
        // A dew bead; after a beat they drop under gravity and dither out.
        const fall = beadAge > 2 ? 1.0 * (beadAge - 2) * (beadAge - 2) * (0.6 + hash(x, 52) * 0.8) : 0;
        level(g, beadAge < 6 ? 3 : beadAge < 9 ? 2 : 1);
        if (fall > s.chest) continue;
        g.fillStyle = C('azure', x % 12 === 0 ? 4 : 5);
        g.fillRect(Math.round(p[0]), Math.round(p[1] + wav + fall), 2, 2);
        px(g, p[0], p[1] + wav + fall, C('white', 8));
      }
      g.globalAlpha = 1;
    }
    // Impact: one fine slit through the foe (a thread's cut), shrinking to its middle; a small star; a few drops.
    const hh = since(s.t, THREAD_HIT);
    const c = strikeAt(s, 0.9);
    if (hh >= 0 && hh < 7) {
      const Lh = 22 - hh * 3;
      level(g, fade(hh, 3, 5));
      line(g, add(c, s.d, -Lh), add(c, s.d, Lh), C('white', 8));
      if (hh < 3) line(g, add(add(c, s.d, -Lh + 4), [0, 1]), add(add(c, s.d, Lh - 4), [0, 1]), C('azure', 4));
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, hh, 'azure', 0.9);
    spray(g, c, hh, 6, 0, 1.4, 2.6, 53, 8);
  },
};

// ── 7. ts-focus (buff): droplets lifted off a blade-ticked ring and held motionless round the waist ──

const FOCUS_N = 8;
const FOCUS_R = 26;
/** Where focus drop i hangs at time t (rising 0.4-0.85, held, falling after 2.05), or null when not shown. */
function focusDrop(s: Scene, i: number): V | null {
  const a = (i / FOCUS_N) * Math.PI * 2 + Math.PI / 8;
  const born = 0.4 + i * 0.03;
  if (s.t < born || s.t >= 2.4) return null;
  const k = clamp01((s.t - born) / 0.4);
  const rise = (1 - (1 - k) * (1 - k)) * s.chest * 0.85;
  const fall = s.t > 2.05 ? 0.5 * Math.pow((s.t - 2.05) * 12, 2) : 0;
  return [s.a[0] + Math.cos(a) * FOCUS_R, s.a[1] + Math.sin(a) * FOCUS_R * 0.5 - rise + Math.min(rise, fall)];
}
const front = (i: number): boolean => Math.sin((i / FOCUS_N) * Math.PI * 2 + Math.PI / 8) > 0;
function focusDrops(g: G, s: Scene, fr: boolean): void {
  for (let i = 0; i < FOCUS_N; i++) {
    if (front(i) !== fr) continue;
    const p = focusDrop(s, i);
    if (!p) continue;
    const held = s.t >= 0.9 && s.t < 2.05;
    // Held drops pulse on the 12 fps clock in turn (a glint passing round the ring), otherwise still.
    const lit = held && (since(s.t, 0.9) + i) % 8 === 0;
    level(g, s.t > 2.25 ? 1 : 3);
    g.fillStyle = C('azure', 4);
    g.fillRect(Math.round(p[0]) - 1, Math.round(p[1]), 3, 2);
    px(g, p[0], p[1] - 1, lit ? C('white', 8) : C('azure', 6));
    px(g, p[0], p[1], lit ? C('white', 8) : C('white', 7));
  }
  g.globalAlpha = 1;
}
/** The thin ring of water joining the held drops (closing with a flash at 0.9), back or front half. */
function focusOrbit(g: G, s: Scene, fr: boolean): void {
  const e = since(s.t, 0.9);
  if (e < 0 || s.t >= 2.05) return;
  const y = s.a[1] - s.chest * 0.85;
  level(g, e < 2 ? 3 : s.t < 1.8 ? 2 : 1);
  isoRing(g, s.a[0], y, FOCUS_R, e < 1 ? C('white', 8) : C('azure', 5), false, fr ? 0 : Math.PI, fr ? Math.PI : Math.PI * 2);
  g.globalAlpha = 1;
}

const focus: ArtFx = {
  id: 'taiji-focus',
  skill: 'ts-focus',
  th: 'ts-focus',
  on: true,
  note: 'วงกระบี่บนพื้นวาดรอบตัว มีขีดปลายกระบี่แปดทิศ -> หยดน้ำแปดหยดลอยขึ้นจากวงมาค้างนิ่งรอบเอว -> วงน้ำบางเชื่อมหยดปิดวงพร้อมแสงวาบ ประกายวิ่งรอบวงทีละหยด (ตั้งรับรอ) -> หยดน้ำร่วงกลับลงวง เป็นวงน้ำเล็ก จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
  hits: [],
  pre: 0.1,
  hit: 0.9,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    if (s.t >= 0.15 && s.t < 2.4) {
      const lv = s.t < 2.0 ? 3 : s.t < 2.2 ? 2 : 1;
      // The ground ring with eight blade ticks pointing out (a compass of sword points), drawn on.
      level(g, Math.min(lv, s.t < 0.9 ? 3 : 2));
      ringOn(g, s.a, FOCUS_R, -Math.PI / 2, (s.t - 0.15) / 0.3, C('azure', 5), true);
      for (let i = 0; i < FOCUS_N; i++) {
        if (s.t < 0.3 + i * 0.03) continue;
        const a = (i / FOCUS_N) * Math.PI * 2;
        const p: V = [s.a[0] + Math.cos(a) * (FOCUS_R + 2), s.a[1] + Math.sin(a) * (FOCUS_R + 2) * 0.5];
        const q: V = [s.a[0] + Math.cos(a) * (FOCUS_R + 6), s.a[1] + Math.sin(a) * (FOCUS_R + 6) * 0.5];
        line(g, p, q, C('white', 6));
        px(g, q[0], q[1], C('white', 8));
      }
      g.globalAlpha = 1;
    }
    for (let i = 0; i < FOCUS_N; i++) {
      const a = (i / FOCUS_N) * Math.PI * 2 + Math.PI / 8;
      ripple(g, [s.a[0] + Math.cos(a) * FOCUS_R, s.a[1] + Math.sin(a) * FOCUS_R * 0.5], since(s.t, 2.18), 1, 1.5, 5);
    }
    focusOrbit(g, s, false);
    focusDrops(g, s, false);
  },
  over(g, s) {
    focusOrbit(g, s, true);
    focusDrops(g, s, true);
    // The blade stilled: a glint running up it as the ring closes, a yin-yang pair at the chest.
    const e = since(s.t, 0.85);
    if (e >= 0 && e < 4) {
      const { hand, tip } = s.limbs(s.t);
      const p = lerp(hand, tip, clamp01((e + 1) / 3));
      level(g, 3);
      diamond(g, p[0], p[1], e < 2 ? 2 : 1, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    if (s.t >= 0.95 && s.t < 2.1) {
      level(g, s.t < 1.9 ? 3 : 2);
      yyPair(g, s.a[0] + 1, s.a[1] - s.chest * 1.15);
      g.globalAlpha = 1;
    }
  },
};

// ── 8. ts-heaven (ท่าใหญ่): a 3.4 s set piece — the area disc, three great water cuts, the seal, the rain ──
// Beats (sample s):
//   0.05-1.10 gather: the area ring drawn on at the target (the tell), the taiji disc formed in it (rim, S, halves),
//             water rising off the caster's feet in a helix to the raised tip;
//   1.20 1.55 1.90 three great cuts across the area (falling, rising, level), each a ground crescent with a wall of
//             water lifted along it, the disc wrenched round a step, split-body afterimages (blows 1-3);
//   2.15      the seal: a dome of water closing over the area from both sides, the gold rim flashing shut;
//   2.45-3.40 the dome breaks into rain, rings where it lands, the disc dithered away.

const H_R = Math.min(72, tilesPx(3));
const H_CUTS = [1.2, 1.55, 1.9] as const;
const H_SEAL = 2.15;
const H_RAIN = 2.45;
const H_END = 3.4;
/** Each cut's ground crescent: start and end angle round the area (screen angles), and its lean. */
const H_ARCS: readonly [number, number][] = [
  [Math.PI * 1.15, Math.PI * 1.95],
  [Math.PI * 0.15, Math.PI * 0.85],
  [Math.PI * 1.9, Math.PI * 1.1],
];
const heavenSpin = (t: number): number => {
  let th = (Math.min(t, H_SEAL) - 0.3) * 0.6;
  for (const c of H_CUTS) if (t >= c) th += Math.PI / 3;
  return th;
};

/** The area's water dome (upright half-ellipse over the disc), back or front half: closing, held, breaking. */
function heavenDome(g: G, s: Scene, fr: boolean): void {
  const e = since(s.t, H_SEAL - 0.1);
  if (e < 0 || s.t >= H_RAIN + 0.1) return;
  const c = s.f;
  const hgt = s.chest * 2.6;
  const k = clamp01((e + 1) / 3);
  const n = 90;
  // Two arcs from the ground at either side rising to the crown; the near half (front) brighter.
  for (let i = 0; i <= n; i++) {
    const u = (i / n) * Math.PI;
    if ((u > Math.PI / 2 ? Math.PI - u : u) > (Math.PI / 2) * k) continue;
    for (const side of [1, -1]) {
      const x = c[0] + side * Math.cos(u) * H_R;
      const y = c[1] - Math.sin(u) * hgt;
      // The outline itself is the far half; the near half is the meridian sweeping over the front.
      if (!fr) {
        level(g, e < 4 ? 3 : 2);
        px(g, x, y, e < 3 ? C('white', 8) : C('azure', 6));
        px(g, x - side, y + 1, C('azure', 4));
      } else {
        const mx = c[0] + side * Math.cos(u) * H_R * 0.45;
        const my = c[1] + H_R * 0.18 - Math.sin(u) * (hgt + H_R * 0.15);
        level(g, e < 3 ? 2 : 1);
        px(g, mx, my, C('azure', 6));
      }
    }
  }
  if (fr && e >= 2 && e < 5) {
    level(g, 3);
    diamond(g, c[0], c[1] - hgt, e === 2 ? 3 : 2, C('azure', 5), C('white', 8));
    line(g, [c[0] - 8, c[1] - hgt], [c[0] + 8, c[1] - hgt], C('white', 8));
  }
  g.globalAlpha = 1;
}

const heaven: ArtFx = {
  id: 'taiji-heaven',
  skill: 'ts-heaven',
  th: 'ts-heaven',
  on: true,
  note: 'ท่าใหญ่: ชูกระบี่ น้ำวนเป็นเกลียวจากเท้าขึ้นปลายกระบี่ วงเขตบนพื้นที่เป้าวาดขึ้น วงหยินหยางก่อตัวในวง -> ฟันใหญ่สามครั้งกวาดผ่านเขต (เฉียงลง เฉียงขึ้น แนวนอน) กำแพงน้ำยกตามคม วงหยินหยางถูกบิดหมุนทุกครั้ง เงาร่างแยก -> โดมน้ำปิดครอบเขตจากสองฝั่ง ขอบทองปิดสนิท (ผนึก) -> โดมแตกเป็นฝน วงน้ำทั่วพื้น วงหยินหยางจางหาย',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 1.1 },
    { m: 'wxCleave', t0: 1.1, t1: 1.38 },
    { m: 'wxRise', t0: 1.4, t1: 1.66 },
    { m: 'wxCleave', t0: 1.72, t1: 2.1 },
    { m: 'wxPower', t0: 2.1, t1: 2.6 },
  ],
  hits: [...H_CUTS],
  big: H_CUTS[2],
  ghosts: H_CUTS.flatMap((t) => [t - 0.06, t - 0.02]),
  stepIn: 8,
  cast: [0.05, 1.0],
  pre: 0.9,
  hit: H_CUTS[0],
  end: H_END,
  afterimage: 'port',
  beats: [
    { t: 0.3, th: 'รวมปราณ วงเขต' },
    { t: H_CUTS[0], th: 'ฟัน 1' },
    { t: H_CUTS[1], th: 'ฟัน 2' },
    { t: H_CUTS[2], th: 'ฟัน 3' },
    { t: H_SEAL, th: 'ผนึก' },
    { t: H_RAIN + 0.15, th: 'ฝนธาร' },
  ],
  under(g, s) {
    const c = s.f;
    // The area: its ring drawn on, a gold rim joining it on the gather's end; held to the rain.
    if (s.t >= 0.1 && s.t < H_END - 0.3) {
      const lv = s.t < H_RAIN + 0.3 ? 3 : s.t < H_END - 0.6 ? 2 : 1;
      level(g, lv);
      ringOn(g, c, H_R, -Math.PI / 2, (s.t - 0.1) / 0.5, C('azure', 5), true);
      if (s.t >= 0.7) {
        const sealed = s.t >= H_SEAL + 0.1;
        ringOn(g, c, H_R + 3, -Math.PI / 2, sealed ? 1 : clamp01((s.t - 0.7) / 0.4) * 0.96, C('gold', sealed && since(s.t, H_SEAL + 0.1) < 2 ? 6 : 4));
      }
      g.globalAlpha = 1;
    }
    // The disc in the area: rim, S line, halves at half strength until the seal, then solid; turned a step per cut.
    if (s.t >= 0.3 && s.t < H_END - 0.4) {
      const ring = clamp01((s.t - 0.3) / 0.45);
      const fill = s.t < 0.85 ? 0 : s.t < 0.95 ? 1 : s.t < H_SEAL + 0.1 ? 2 : s.t < H_RAIN + 0.35 ? 3 : s.t < H_END - 0.7 ? 1 : 0;
      taijiDisc(g, c, Math.round(H_R * 0.55), heavenSpin(s.t), fill, s.t < H_RAIN + 0.35 ? ring : 0, s.t >= 0.75, s.t >= 0.95 && s.t < H_END - 0.7);
    }
    // The cuts' scars: each ground crescent left as a dithered groove until the rain.
    H_CUTS.forEach((at, i) => {
      const e = since(s.t, at);
      if (e < 2 || s.t >= H_RAIN + 0.4) return;
      const [a0, a1] = H_ARCS[i]!;
      level(g, 1);
      crescent(g, c[0], c[1], H_R * 0.85, a0, a1, 1, C('azure', 3), C('azure', 3), 0.5, false);
      g.globalAlpha = 1;
    });
    // The rain: rings landing all over the area.
    for (let i = 0; i < 12; i++) {
      const a = hash(i, 61) * Math.PI * 2;
      const r = Math.sqrt(hash(i, 62)) * H_R * 0.9;
      ripple(g, [c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r * 0.5], since(s.t, H_RAIN + 0.15 + hash(i, 63) * 0.5), 2, 2, 6);
    }
    heavenDome(g, s, false);
  },
  over(g, s) {
    const c = s.f;
    // Gather: water rising off the caster's feet in a helix to the raised tip.
    if (s.t >= 0.15 && s.t < 1.1) {
      const tip = s.limbs(Math.min(s.t, 1.0)).tip;
      for (let i = 0; i < 12; i++) {
        const u = ((s.t - 0.15) * 1.4 + i / 12) % 1;
        const a = u * Math.PI * 5 + i * 0.4;
        const r = 16 * (1 - u) + 2;
        const p: V = [lerp(s.a, tip, u)[0] + Math.cos(a) * r, lerp(s.a, tip, u)[1] + Math.sin(a) * r * 0.45];
        if (Math.sin(a) < 0 && u < 0.7) continue;
        level(g, s.t < 0.3 || s.t > 1.0 ? 2 : 3);
        drop(g, p[0], p[1], i % 3 === 0);
      }
      g.globalAlpha = 1;
    }
    glint(g, s, 0.8, 1.15);
    // Three great cuts: a ground crescent across the area (fresh: white band 4 deep, drawn over two frames) with a
    // wall of water lifted along it, and an upright crescent through the foe for the read.
    H_CUTS.forEach((at, i) => {
      bladeArc(g, s, at - 0.12, at + 0.2, 'azure', 4);
      const e = since(s.t, at - 0.08);
      const [a0, a1] = H_ARCS[i]!;
      if (e >= 0 && e < 6) {
        const k = e === 0 ? 0.55 : 1;
        const R0 = H_R * 0.85;
        loopBand(g, c, R0, R0 * 0.5, a0, a0 + (a1 - a0) * k, e < 3 ? 5 : 2, e < 3 ? 3 : 2);
      }
      const w = since(s.t, at);
      if (w >= 0 && w < 8) {
        // The water wall: columns of drops thrown up along the crescent, falling back.
        level(g, fade(w, 4, 6));
        for (let j = 0; j < 16; j++) {
          const a = a0 + ((a1 - a0) * (j + 0.5)) / 16;
          const b: V = [c[0] + Math.cos(a) * H_R * 0.85, c[1] + Math.sin(a) * H_R * 0.85 * 0.5];
          const v = 3 + hash(j, 70 + i) * 2;
          const hgt = Math.max(0, v * w - 0.45 * w * w);
          drop(g, b[0], b[1] - hgt, j % 2 === 0);
          if (hgt > 5) drop(g, b[0] + (j % 2 ? 1 : -1), b[1] - hgt * 0.55, true);
          if (hgt > 4) px(g, b[0], b[1] - hgt + 3, C('azure', 4));
        }
        g.globalAlpha = 1;
        const lean = i === 1 ? -1 : 1;
        if (w < 4) {
          level(g, w < 2 ? 3 : 2);
          const sc = strikeAt(s);
          crescent(g, sc[0] - 6 * lean, sc[1], 16, -Math.PI / 2 - 0.9 * lean, Math.PI / 2 - 0.9 * lean, w < 2 ? 3 : 1, C('white', 8), C('azure', 4), 1, true);
          g.globalAlpha = 1;
        }
      }
      hitStar(g, s, strikeAt(s), w, 'azure', i === 2 ? 1.4 : 1);
    });
    heavenDome(g, s, true);
    // The rain: the dome's water falling back over the area, a few yin-yang pairs drifting down with it.
    const e = since(s.t, H_RAIN);
    if (e >= 0 && s.t < H_END) {
      for (let i = 0; i < 22; i++) {
        const a = hash(i, 64) * Math.PI * 2;
        const r = Math.sqrt(hash(i, 65)) * H_R * 0.9;
        const gx = c[0] + Math.cos(a) * r;
        const gy = c[1] + Math.sin(a) * r * 0.5;
        const t0 = hash(i, 63) * 0.5;
        const k = (s.t - H_RAIN - t0 + 0.15) / 0.3;
        if (k < 0 || k > 1) continue;
        const y = gy - (1 - k) * (s.chest * 2.4);
        level(g, s.t < H_END - 0.5 ? 3 : 2);
        if (i % 7 === 0) yyPair(g, gx, y);
        else {
          px(g, gx, y, C('white', 8));
          px(g, gx, y - 1, C('azure', 5));
          px(g, gx, y - 2, C('azure', 4));
        }
      }
      g.globalAlpha = 1;
    }
    pairs(g, [c[0], c[1] - s.chest], since(s.t, H_SEAL + 0.1), 6, 66, 12);
  },
};

export const TAIJI_FX: readonly ArtFx[] = [circle, yinyang, stars, still, flow, thread, focus, heaven];
